import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  createPromptModule,
  listPromptModules,
  setPromptModuleDefault,
  type PromptModule,
  type PromptModuleStatus,
} from "@/lib/api";
import {
  Badge,
  Button,
  ErrorBanner,
  Field,
  Modal,
  PageContainer,
  Select,
  TextInput,
  toErrorMessage,
} from "@/components/Forms";
import { PlusIcon } from "@/components/Icons";

const VERTICALS = ["fitness", "solar", "roofing", "real_estate"];

/**
 * Super-admin catalog of prompt modules. Modules are bundles of
 * template bodies per vertical; a tenant gets ASSIGNED one module via
 * the Tenants page picker, and the engine renders its bodies in
 * preference to the JAR defaults.
 *
 * <p>Rows are grouped by vertical so the operator can see "fitness has
 * 3 modules, 1 published" at a glance.
 */
export default function PromptModulesPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<PromptModule[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [reloadAt, setReloadAt] = useState(0);
  const [actioning, setActioning] = useState<string | null>(null);

  async function makeDefault(id: string) {
    setActioning(id);
    setError(null);
    try {
      await setPromptModuleDefault(id);
      setReloadAt((n) => n + 1);
    } catch (e) {
      setError(toErrorMessage(e, t("promptModules.setDefaultError")));
    } finally {
      setActioning(null);
    }
  }

  useEffect(() => {
    let cancelled = false;
    listPromptModules()
      .then((r) => !cancelled && setRows(r.modules))
      .catch((e) => !cancelled && setError(toErrorMessage(e, t("promptModules.loadError"))));
    return () => {
      cancelled = true;
    };
  }, [reloadAt, t]);

  const grouped: Record<string, PromptModule[]> = {};
  if (rows) {
    for (const m of rows) {
      (grouped[m.vertical] ??= []).push(m);
    }
  }

  return (
    <PageContainer
      title={t("promptModules.title")}
      subtitle={t("promptModules.subtitle")}
      actions={
        <Button variant="primary" onClick={() => setCreating(true)}>
          <PlusIcon size={14} /> {t("promptModules.newModule")}
        </Button>
      }
    >
      <ErrorBanner message={error} />

      {rows === null ? (
        <p className="text-sm text-muted-foreground">{t("forms.loading")}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("promptModules.empty")}</p>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([vertical, list]) => (
            <section key={vertical} className="rounded-lg border border-border bg-card">
              <header className="flex items-center justify-between px-4 py-2 border-b border-border">
                <h2 className="font-mono text-sm">{vertical}</h2>
                <Link
                  to={`/verticals/${vertical}/facts-schema`}
                  className="text-xs text-accent hover:underline"
                >
                  {t("promptModules.editFactsSchema")}
                </Link>
              </header>
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2">{t("promptModules.colName")}</th>
                    <th className="text-left px-4 py-2 hidden md:table-cell">
                      {t("promptModules.colDescription")}
                    </th>
                    <th className="text-left px-4 py-2">{t("promptModules.colStatus")}</th>
                    <th className="text-left px-4 py-2 hidden md:table-cell">
                      {t("promptModules.colAllowlistCount")}
                    </th>
                    <th className="text-right px-4 py-2 hidden md:table-cell">
                      {t("promptModules.colUpdated")}
                    </th>
                    <th className="text-right px-4 py-2">{t("promptModules.colActions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {list.map((m) => (
                    <tr key={m.id} className="hover:bg-muted/30">
                      <td className="px-4 py-2">
                        <Link
                          to={`/prompt-modules/${m.id}`}
                          className="font-medium text-accent hover:underline"
                        >
                          {m.name}
                        </Link>
                        {m.isDefault && (
                          <span className="ml-2 rounded bg-accent/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-accent">
                            {t("promptModules.defaultBadge")}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 hidden md:table-cell text-xs text-muted-foreground">
                        {m.description ?? "—"}
                      </td>
                      <td className="px-4 py-2">
                        <StatusBadge status={m.status} />
                      </td>
                      <td className="px-4 py-2 hidden md:table-cell text-xs text-muted-foreground">
                        {m.allowlistTenantCount && m.allowlistTenantCount > 0
                          ? t("promptModules.allowlistTenantCount", {
                              count: m.allowlistTenantCount,
                            })
                          : t("promptModules.allowlistTenantNone")}
                      </td>
                      <td className="px-4 py-2 hidden md:table-cell text-right text-xs text-muted-foreground">
                        {m.updatedAt ?? "—"}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {m.isDefault ? (
                          <span className="text-xs text-muted-foreground">
                            {t("promptModules.isDefault")}
                          </span>
                        ) : (
                          <Button onClick={() => makeDefault(m.id)} disabled={actioning === m.id}>
                            {actioning === m.id ? t("forms.saving") : t("promptModules.setDefault")}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </div>
      )}

      {creating && (
        <CreateModuleModal
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            setReloadAt((n) => n + 1);
          }}
        />
      )}
    </PageContainer>
  );
}

function StatusBadge({ status }: { status: PromptModuleStatus }) {
  const tone = status === "PUBLISHED" ? "success" : status === "DRAFT" ? "neutral" : "danger";
  return <Badge tone={tone}>{status}</Badge>;
}

interface CreateProps {
  onClose: () => void;
  onCreated: () => void;
}

function CreateModuleModal({ onClose, onCreated }: CreateProps) {
  const { t } = useTranslation();
  const [vertical, setVertical] = useState("fitness");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<PromptModuleStatus>("DRAFT");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!name.trim()) {
      setErr(t("promptModules.errorNameRequired"));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await createPromptModule({ vertical, name: name.trim(), description: description.trim() || null, status });
      onCreated();
    } catch (e) {
      setErr(toErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={true} title={t("promptModules.newModule")} onClose={onClose}>
      <ErrorBanner message={err} />
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t("promptModules.fieldVertical")} required>
          <Select value={vertical} onChange={(e) => setVertical(e.target.value)}>
            {VERTICALS.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("promptModules.fieldName")} required>
          <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="warm-default" />
        </Field>
      </div>
      <Field label={t("promptModules.fieldDescription")}>
        <TextInput value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <Field label={t("promptModules.fieldStatus")}>
        <Select value={status} onChange={(e) => setStatus(e.target.value as PromptModuleStatus)}>
          <option value="DRAFT">DRAFT</option>
          <option value="PUBLISHED">PUBLISHED</option>
          <option value="ARCHIVED">ARCHIVED</option>
        </Select>
      </Field>
      <div className="mt-3 flex justify-end gap-2">
        <Button onClick={onClose} disabled={busy}>
          {t("forms.cancel")}
        </Button>
        <Button variant="primary" onClick={submit} disabled={busy}>
          {busy ? t("forms.saving") : t("forms.create")}
        </Button>
      </div>
    </Modal>
  );
}
